@if (vacancy) {
  <nb-card [formGroup]="form">
    <nb-card-header>
      <div class="header-container">
        <div class="header-left">
          <button nbButton status="basic" size="small" (click)="goBack()">
            <nb-icon icon="arrow-back-outline"></nb-icon>
            {{ (embedded ? 'close' : 'Back') | translate }}
          </button>
          @if (!editing) {
            <h5>{{ vacancy.title }}</h5>
          }
          @if (editing) {
            <input nbInput formControlName="title" fieldSize="medium" class="title-input">
          }
        </div>
        <div class="header-actions">
          @if (!editing) {
            <button nbButton
              [nbTooltip]="'vacancy.expired' | translate"
              [nbTooltipDisabled]="!isExpired()"
              [status]="hasInterest() ? 'basic' : 'primary'"
              size="small"
              (click)="toggleInterest()"
              [disabled]="isLoading">
              <nb-icon [icon]="hasInterest() ? 'heart' : 'heart-outline'"></nb-icon>
              {{ (hasInterest() ? 'vacancy.withdraw' : 'vacancy.express') | translate }}
            </button>
            @if (tailoringEnabled) {
              <button nbButton status="primary" size="small" shape="semi-round" type="button" (click)="createDocuments()">
                <nb-icon icon="file-add-outline"></nb-icon>{{ 'cv.hub.open' | translate }}
              </button>
            }
            @if (canCreateDeal) {
              @if (!vacancy.crm_opportunity_id && dealQueuedFor !== vacancy.id) {
                <button nbButton status="primary" size="small" shape="semi-round" type="button"
                  (click)="createDeal()" [disabled]="isLoading">
                  <nb-icon icon="briefcase-outline"></nb-icon>
                  {{ 'crm.create_deal' | translate }}
                </button>
              } @else {
                <span class="crm-deal-chip" [nbTooltip]="'crm.deal_exists_tooltip' | translate:{ id: vacancy.crm_opportunity_id }">
                  <nb-icon icon="checkmark-circle-2-outline"></nb-icon>
                  {{ 'crm.deal_chip' | translate }}
                </span>
                <button nbButton ghost size="small" shape="semi-round" type="button" (click)="viewDeal()">
                  <nb-icon icon="external-link-outline"></nb-icon>
                  {{ 'crm.view_deal' | translate }}
                </button>
              }
            }
            <button nbButton ghost size="small"
              [nbPopover]="barMenuTpl" nbPopoverTrigger="click" nbPopoverPlacement="bottom"
              #barPop="nbPopover"
              [attr.aria-label]="'options' | translate">
              <nb-icon icon="more-vertical-outline"></nb-icon>
            </button>
            <ng-template #barMenuTpl>
              <div class="kebab-popup">
                @for (it of barMenu; track it.data.action) {
                  <button class="kebab-item" [class.kebab-item--danger]="it.data.action === 'delete'"
                          (click)="runBarAction(it.data.action)">
                    {{ it.title }}
                  </button>
                }
              </div>
            </ng-template>
          }
          @if (editing) {
            <button nbButton size="small" status="basic" (click)="cancelEdit()">{{ 'cancel' | translate }}</button>
            <button nbButton size="small" status="primary" (click)="saveVacancy()">
              <nb-icon icon="checkmark-outline"></nb-icon> {{ 'save' | translate }}
            </button>
          }
        </div>
      </div>
    </nb-card-header>
    <nb-card-body>
      <!-- Status Badges -->
      <div class="badges-row">
        <div class="badge-wrapper">
          <nb-badge [text]="isIntake ? ('intake.verdict.direct_request' | translate) : vacancy.source" status="info"></nb-badge>
        </div>
        @if (vacancy.status === 1) {
          <div class="badge-wrapper">
            <nb-badge text="Active" status="success"></nb-badge>
          </div>
        }
        @if (vacancy.status === 0) {
          <div class="badge-wrapper">
            <nb-badge text="Inactive" status="danger"></nb-badge>
          </div>
        }
      </div>
      <!-- Main Content Grid -->
      <div class="content-grid">
        <!-- Left Column: Details -->
        <div class="details-section">
          <h6><nb-icon icon="info-outline"></nb-icon>{{ 'Details' | translate }}</h6>

          <!-- Company -->
          @if (!editing) {
            @if (vacancy.company) {
              <div class="detail-row">
                <span class="detail-label">{{ 'Company' | translate }}:</span>
                <span class="detail-value">{{ vacancy.company }}</span>
              </div>
            }
          }
          @if (editing) {
            <div class="detail-row">
              <span class="detail-label">{{ 'Company' | translate }}:</span>
              <input nbInput fieldSize="small" formControlName="company" class="detail-input">
            </div>
          }

          <!-- Location -->
          @if (!editing) {
            @if (vacancy.location) {
              <div class="detail-row">
                <span class="detail-label">{{ 'Location' | translate }}:</span>
                <span class="detail-value">{{ vacancy.location }}</span>
              </div>
            }
          }
          @if (editing) {
            <div class="detail-row">
              <span class="detail-label">{{ 'Location' | translate }}:</span>
              <input nbInput fieldSize="small" formControlName="location" class="detail-input">
            </div>
          }

          <!-- Industry -->
          @if (!editing) {
            @if (vacancy.industry) {
              <div class="detail-row">
                <span class="detail-label">{{ 'Industry' | translate }}:</span>
                <span class="detail-value">{{ vacancy.industry }}</span>
              </div>
            }
          }
          @if (editing) {
            <div class="detail-row">
              <span class="detail-label">{{ 'Industry' | translate }}:</span>
              <input nbInput fieldSize="small" formControlName="industry" class="detail-input">
            </div>
          }

          <!-- Posting Date -->
          @if (!editing) {
            @if (vacancy.posting_date) {
              <div class="detail-row">
                <span class="detail-label">{{ 'Posted' | translate }}:</span>
                <span class="detail-value">{{ vacancy.posting_date | date: 'd MMM y' }}</span>
              </div>
            }
          }
          @if (editing) {
            <div class="detail-row">
              <span class="detail-label">{{ 'Posted' | translate }}:</span>
              <input nbInput fieldSize="small" [nbDatepicker]="editPostingPicker" formControlName="posting_date" class="detail-input">
              <nb-datepicker #editPostingPicker></nb-datepicker>
            </div>
          }

          <!-- Deadline -->
          @if (!editing) {
            @if (vacancy.deadline) {
              <div class="detail-row">
                <span class="detail-label">{{ 'Deadline' | translate }}:</span>
                <span class="detail-value" [class.text-danger]="isExpired()">
                  {{ vacancy.deadline | date: 'd MMM y' }}
                </span>
              </div>
            }
          }
          @if (editing) {
            <div class="detail-row">
              <span class="detail-label">{{ 'Deadline' | translate }}:</span>
              <input nbInput fieldSize="small" [nbDatepicker]="editDeadlinePicker" formControlName="deadline" class="detail-input">
              <nb-datepicker #editDeadlinePicker></nb-datepicker>
            </div>
          }

          <!-- Start Date -->
          @if (!editing) {
            @if (vacancy.start_date) {
              <div class="detail-row">
                <span class="detail-label">{{ 'Start date' | translate }}:</span>
                <span class="detail-value">{{ vacancy.start_date | date: 'd MMM y' }}</span>
              </div>
            }
          }
          @if (editing) {
            <div class="detail-row">
              <span class="detail-label">{{ 'Start date' | translate }}:</span>
              <input nbInput fieldSize="small" [nbDatepicker]="editStartPicker" formControlName="start_date" class="detail-input">
              <nb-datepicker #editStartPicker></nb-datepicker>
            </div>
          }

          <!-- End Date -->
          @if (!editing) {
            @if (vacancy.end_date) {
              <div class="detail-row">
                <span class="detail-label">{{ 'End date' | translate }}:</span>
                <span class="detail-value">{{ vacancy.end_date | date: 'd MMM y' }}</span>
              </div>
            }
          }
          @if (editing) {
            <div class="detail-row">
              <span class="detail-label">{{ 'End date' | translate }}:</span>
              <input nbInput fieldSize="small" [nbDatepicker]="editEndPicker" formControlName="end_date" class="detail-input">
              <nb-datepicker #editEndPicker></nb-datepicker>
            </div>
          }

          <!-- Hours/Week -->
          @if (!editing) {
            @if (vacancy.hours_per_week) {
              <div class="detail-row">
                <span class="detail-label">{{ 'Hours/Week' | translate }}:</span>
                <span class="detail-value">{{ vacancy.hours_per_week }}</span>
              </div>
            }
          }
          @if (editing) {
            <div class="detail-row">
              <span class="detail-label">{{ 'Hours/Week' | translate }}:</span>
              <input nbInput fieldSize="small" formControlName="hours_per_week" class="detail-input">
            </div>
          }

          <!-- Duration -->
          @if (!editing) {
            @if (vacancy.contract_duration) {
              <div class="detail-row">
                <span class="detail-label">{{ 'Duration' | translate }}:</span>
                <span class="detail-value">{{ vacancy.contract_duration }}</span>
              </div>
            }
          }
          @if (editing) {
            <div class="detail-row">
              <span class="detail-label">{{ 'Duration' | translate }}:</span>
              <input nbInput fieldSize="small" formControlName="contract_duration" class="detail-input">
            </div>
          }

          <!-- Tariff -->
          @if (!editing) {
            @if (vacancy.tariff) {
              <div class="detail-row">
                <span class="detail-label">{{ 'Tariff' | translate }}:</span>
                <span class="detail-value">{{ vacancy.tariff }}</span>
              </div>
            }
          }
          @if (editing) {
            <div class="detail-row">
              <span class="detail-label">{{ 'Tariff' | translate }}:</span>
              <input nbInput fieldSize="small" formControlName="tariff" class="detail-input">
            </div>
          }

          <!-- Experience -->
          @if (!editing) {
            @if (vacancy.years_experience && !conditionView?.hideYearsString) {
              <div class="detail-row">
                <span class="detail-label">{{ 'Experience' | translate }}:</span>
                <span class="detail-value">{{ vacancy.years_experience }}</span>
              </div>
            }
          }
          @if (editing) {
            <div class="detail-row">
              <span class="detail-label">{{ 'Experience' | translate }}:</span>
              <input nbInput fieldSize="small" formControlName="years_experience" class="detail-input">
            </div>
          }

          <!-- URL -->
          @if (!editing) {
            @if (vacancy.url) {
              <div class="detail-row">
                <span class="detail-label">{{ 'External link' | translate }}:</span>
                <span class="detail-value">
                  <a (click)="openUrl()" class="external-link">
                    {{ isIntake ? ('intake.detail.portal_link' | translate) : 'View on ' + vacancy.source }}
                    <nb-icon icon="external-link-outline"></nb-icon>
                  </a>
                  @if (isIntake && linkHost) {
                    <span class="link-host">{{ linkHost }}</span>
                  }
                </span>
              </div>
            }
            @if (isIntake && vacancy.intake_mail_id && canManage) {
              <div class="detail-row">
                <span class="detail-label">{{ 'intake.detail.mail_label' | translate }}:</span>
                <span class="detail-value">
                  <a [routerLink]="['/market/intake', vacancy.intake_mail_id]" class="external-link">
                    {{ 'intake.detail.open_mail' | translate }}
                    <nb-icon icon="email-outline"></nb-icon>
                  </a>
                </span>
              </div>
            }
          }
          @if (editing) {
            <div class="detail-row">
              <span class="detail-label">{{ 'External link' | translate }}:</span>
              <input nbInput fieldSize="small" formControlName="url" class="detail-input">
            </div>
          }

          <!--
            The same opening on other platforms (#1197). Absent when the server
            sends none, which means "no sibling we can vouch for" rather than
            "posted nowhere else" - the list is title-filtered because canonical
            groups over-merge. Deliberately says nothing in that case rather
            than implying uniqueness.
          -->
          @if (!editing && vacancy.group_members?.length) {
            <div class="detail-row sibling-postings">
              <span class="detail-label">{{ 'vacancy.also_posted_on' | translate }}:</span>
              <span class="detail-value">
                @for (sib of vacancy.group_members; track sib.id) {
                  <a class="external-link sibling-posting"
                     [class.sibling-expired]="isSiblingExpired(sib)"
                     [href]="sib.url" target="_blank" rel="noopener noreferrer"
                     [attr.title]="sib.title">
                    {{ sib.source }}
                    @if (isSiblingExpired(sib)) { <span class="sibling-note">({{ 'vacancy.closed' | translate }})</span> }
                    <nb-icon icon="external-link-outline"></nb-icon>
                  </a>
                }
              </span>
            </div>
          }

          <!-- Matching Users Section -->
          <div class="matching-users-section mt-4">
            <div class="section-header">
              <h6>{{ 'Matching users' | translate }}</h6>
              @if (matchScore?.score !== null && matchScore?.score !== undefined) {
                <span class="overall-score-badge"
                  [class]="'score-' + getScoreColor(matchScore.score)">
                  {{ 'Best match score' | translate }}: {{ matchScore.score.toFixed(0) }}/100
                </span>
              }
            </div>
            @if (matchingUsersLoading) {
              <div class="loading-indicator">
                <nb-spinner size="small" status="primary"></nb-spinner>
              </div>
            }
            @if (!matchingUsersLoading && matchScore?.needs_enrichment) {
              <div class="needs-enrichment-notice">
                <nb-icon icon="alert-triangle-outline" status="warning"></nb-icon>
                <span>{{ 'Vacancy needs skill enrichment to compute matches' | translate }}</span>
              </div>
            }
            @if (!matchingUsersLoading && !matchScore?.needs_enrichment && matchingUsers.length === 0) {
              <div class="no-matches">
                <span class="text-muted">{{ 'No matching users found' | translate }}</span>
              </div>
            }
            @if (!matchingUsersLoading && matchingUsers.length > 0) {
              <div class="matching-users-grid">
                @for (match of matchingUsers.slice(0, 5); track match; let i = $index) {
                  <div class="user-card" (click)="goToUser(match.User_id)">
                    <div class="card-header-row">
                      <div class="user-info">
                        <nb-user
                          [name]="getUserName(match)"
                          [picture]="getUserPicture(match)"
                          size="medium"
                          [onlyPicture]="false"
                          class="user-avatar">
                        </nb-user>
                      </div>
                      @if (match.score !== null) {
                        <div class="score-badge"
                          [class]="'score-' + getScoreColor(match.score)">
                          {{ match.score.toFixed(0) }}/100
                        </div>
                      }
                      <bst-description-fit-warning [show]="!!match?.match_details?.description_fit_warning"></bst-description-fit-warning>
                    </div>
                    <div class="card-divider"></div>
                    <bst-eligibility-summary
                      [eligibility]="match.eligibility"
                      [pending]="eligibilityPending && match.eligibility?.verdict === 'not_checked'">
                    </bst-eligibility-summary>
                    @if (getUserFactors(match).length > 0) {
                      <div class="factors-grid">
                        @for (f of getUserFactors(match); track f) {
                          <div class="factor-item"
                            [nbTooltip]="f.notes" nbTooltipStatus="basic">
                            <div class="factor-header">
                              <span class="factor-label">{{ f.label }}</span>
                              <span class="factor-value" [class]="'text-' + getFactorBarColor(f.score)">{{ (f.score * 100).toFixed(0) }}%</span>
                            </div>
                            <div class="factor-bar-track">
                              <div class="factor-bar-fill"
                                [class]="'bar-' + getFactorBarColor(f.score)"
                                [style.width.%]="f.score * 100">
                              </div>
                            </div>
                          </div>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            }
            @if (vacancy?.team_interests?.length) {
              <div class="detail-team-interest">
                <span class="detail-team-interest-label">{{ 'vacancy.interestedColleagues' | translate }}</span>
                <div class="detail-team-interest-avatars">
                  @for (interest of vacancy.team_interests.slice(0, 8); track interest.id) {
                    <nb-user
                      size="small"
                      [onlyPicture]="true"
                      [name]="getUserName(interest)"
                      [picture]="getUserPicture(interest)"
                      [nbTooltip]="getUserName(interest)"
                      (click)="goToUser(interest.User_id)"
                      class="detail-team-interest-avatar">
                    </nb-user>
                  }
                  @if (vacancy.team_interests.length > 8) {
                    <span class="detail-team-interest-more" [nbTooltip]="teamOverflowNames()">+{{ vacancy.team_interests.length - 8 }}</span>
                  }
                </div>
              </div>
            }
          </div>
        </div>
        <!-- Right Column: Description & Skills -->
        <div class="description-section">
          @if (vacancy.ai_summary) {
            <div class="ai-summary-section">
              <h6>{{ 'Summary' | translate }}</h6>
              <p class="ai-summary">{{ vacancy.ai_summary }}</p>
            </div>
          }
          <h6>{{ 'Description' | translate }}</h6>
          @if (!editing) {
            @if (vacancy.description) {
              <div class="description-content" [innerHTML]="vacancy.description | formatDescription"></div>
            }
            @if (!vacancy.description) {
              <div class="description-content">
                <p class="text-muted">{{ 'No description available' | translate }}</p>
              </div>
            }
          }
          @if (editing) {
            <textarea nbInput fullWidth formControlName="description" rows="8"></textarea>
            <div class="ai-fill-section">
              <div class="ai-fill-header">
                <button nbButton size="small" type="button"
                  status="info"
                  [disabled]="filling || !form.get('description')?.value"
                  [nbTooltip]="'vacancy.fillwithai.tooltip' | translate"
                  [class.loading]="filling"
                  (click)="fillWithAI()">
                  @if (!filling) {
                    <nb-icon icon="bulb-outline"></nb-icon>
                  }
                  @if (filling) {
                    <nb-spinner size="tiny" status="control"></nb-spinner>
                  }
                  {{ (filling ? 'vacancy.fillwithai.loading' : 'vacancy.fillwithai.label') | translate }}
                </button>
              </div>
              <div class="ai-privacy-toggle" (click)="privacyExpanded = !privacyExpanded">
                <nb-icon icon="shield-outline"></nb-icon>
                <span>{{ 'vacancy.fillwithai.privacy.toggle' | translate }}</span>
                <nb-icon [icon]="privacyExpanded ? 'chevron-up-outline' : 'chevron-down-outline'"></nb-icon>
              </div>
              @if (privacyExpanded) {
                <div class="ai-privacy-details">
                  <div class="privacy-section">
                    <strong>{{ 'vacancy.fillwithai.privacy.what_title' | translate }}</strong>
                    <p>{{ 'vacancy.fillwithai.privacy.what_text' | translate }}</p>
                  </div>
                  <div class="privacy-section">
                    <strong>{{ 'vacancy.fillwithai.privacy.how_title' | translate }}</strong>
                    <p>{{ 'vacancy.fillwithai.privacy.how_text' | translate }}</p>
                  </div>
                  <div class="privacy-section">
                    <strong>{{ 'vacancy.fillwithai.privacy.who_title' | translate }}</strong>
                    <p>{{ 'vacancy.fillwithai.privacy.who_text' | translate }}</p>
                  </div>
                  <div class="privacy-section">
                    <strong>{{ 'vacancy.fillwithai.privacy.retention_title' | translate }}</strong>
                    <p>{{ 'vacancy.fillwithai.privacy.retention_text' | translate }}</p>
                  </div>
                  <div class="privacy-section">
                    <strong>{{ 'vacancy.fillwithai.privacy.rights_title' | translate }}</strong>
                    <p>{{ 'vacancy.fillwithai.privacy.rights_text' | translate }}</p>
                  </div>
                  <div class="privacy-section">
                    <strong>{{ 'vacancy.fillwithai.privacy.transparency_title' | translate }}</strong>
                    <p>{{ 'vacancy.fillwithai.privacy.transparency_text' | translate }}</p>
                  </div>
                  <div class="privacy-footer">
                    <a href="assets/files/flix_it_privacy_policy.pdf" target="_blank">{{ 'vacancy.fillwithai.privacy.full_policy' | translate }}</a>
                  </div>
                </div>
              }
            </div>
          }

          @if (!editing && conditionView) {
            <section class="cond" id="cond">
              <div class="sec-head">
                <h6>{{ 'vacancy.conditions_title' | translate }}</h6>
                <button type="button" class="link-btn" [attr.aria-expanded]="showQuotes" (click)="showQuotes = !showQuotes">
                  {{ 'vacancy.conditions_source' | translate }}
                  <nb-icon icon="chevron-down-outline" aria-hidden="true"></nb-icon>
                </button>
              </div>
              <ul class="legend">
                @if (conditionView.state === 'full') {
                  <li><span class="chip requirement">{{ 'vacancy.conditions_eis' | translate }}</span><span>{{ 'vacancy.conditions_eis_hint' | translate }}</span></li>
                }
                <li><span class="chip wish">{{ 'vacancy.conditions_wens' | translate }}</span><span>{{ 'vacancy.conditions_wens_hint' | translate }}</span></li>
              </ul>
              @if (conditionView.incomplete) {
                <p class="text-muted">{{ 'vacancy.conditions_incomplete' | translate }}</p>
              }
              @if (conditionView.state === 'wishes') {
                <p class="text-muted">{{ 'vacancy.conditions_none' | translate }}</p>
              }
              @for (row of conditionView.rows; track row.key) {
                <div class="detail-row">
                  <span class="detail-label">{{ row.label | translate }}</span>
                  <div class="detail-value">
                    <div class="skills-badges">
                      @for (c of row.chips; track $index) {
                        <span [class.chip-note]="c.notChecked">
                          <span class="chip" [class.requirement]="c.eis" [class.wish]="!c.eis">
                            <span class="sr">{{ (c.eis ? 'vacancy.conditions_eis' : 'vacancy.conditions_wens') | translate }}: </span>
                            <span>{{ c.text }}</span>
                          </span>
                          @if (c.notChecked) {<span class="cond-flag"><nb-icon icon="minus-circle-outline" aria-hidden="true"></nb-icon>{{ 'vacancy.conditions_not_checked' | translate }}</span>}
                        </span>
                      }
                    </div>
                    @if (showQuotes) {
                      @for (c of row.chips; track $index) {
                        @if (c.quote) {<span class="cond-quote">{{ c.text }} · “{{ c.quote }}”</span>}
                      }
                    }
                  </div>
                </div>
              }
            </section>
          }
          <!-- Hard Skills -->
          @if (editing || !conditionView) {
            <div class="mt-4">
              <h6>{{ 'hardskills' | translate }}</h6>
              @if (!editing) {
                @if (vacancy.skills?.length) {
                  <div class="skills-badges">
                    @for (skill of vacancy.skills; track skill) {
                      <span class="chip">{{ skill.name }}</span>
                    }
                  </div>
                }
                @if (!vacancy.skills?.length) {
                  <span class="text-muted">-</span>
                }
              }
              @if (editing) {
                <bst-list-adder target="skill" [autolist]="true" formControlName="skills"></bst-list-adder>
              }
            </div>
          }

          <!-- Certifications -->
          @if (editing || !conditionView) {
            <div class="mt-4">
              <h6>{{ 'certifications' | translate }}</h6>
              @if (!editing) {
                @if (vacancy.certifications?.length) {
                  <div class="skills-badges">
                    @for (cert of vacancy.certifications; track cert) {
                      <span class="chip">{{ cert.name }}</span>
                    }
                  </div>
                }
                @if (!vacancy.certifications?.length) {
                  <span class="text-muted">-</span>
                }
              }
              @if (editing) {
                <bst-list-adder target="certification" [autolist]="true" formControlName="certifications"></bst-list-adder>
              }
            </div>
          }

          <!-- Soft Skills -->
          @if (editing || !conditionView) {
            <div class="mt-4">
              <h6>{{ 'softskills' | translate }}</h6>
              @if (!editing) {
                @if (vacancy.developments?.length) {
                  <div class="skills-badges">
                    @for (dev of vacancy.developments; track dev) {
                      <span class="chip">{{ dev.name }}</span>
                    }
                  </div>
                }
                @if (!vacancy.developments?.length) {
                  <span class="text-muted">-</span>
                }
              }
              @if (editing) {
                <bst-list-adder target="development" [autolist]="true" formControlName="developments"></bst-list-adder>
              }
            </div>
          }

          <!-- Industries -->
          <div class="mt-4">
            <h6>{{ 'industries' | translate }}</h6>
            @if (!editing) {
              @if (vacancy.industries?.length) {
                <div class="skills-badges">
                  @for (ind of vacancy.industries; track ind) {
                    <span class="chip">{{ ind.name }}</span>
                  }
                </div>
              }
              @if (!vacancy.industries?.length) {
                <span class="text-muted">-</span>
              }
            }
            @if (editing) {
              <bst-list-adder target="industry" [autolist]="true" formControlName="industries_list"></bst-list-adder>
            }
          </div>

          <!-- Domains: machine-derived only, never editable here -->
          <div class="mt-4">
            <h6>{{ 'userdomains' | translate }}</h6>
            @if (vacancy.domains?.length) {
              <div class="skills-badges">
                @for (dom of vacancy.domains; track dom) {
                  <span class="chip">{{ dom.name }}</span>
                }
              </div>
            }
            @if (!vacancy.domains?.length) {
              <span class="text-muted">-</span>
            }
          </div>
        </div>
      </div>
      <!-- Metadata Footer -->
      <div class="metadata-footer">
        @if (vacancy.created_at) {
          <div class="metadata-item">
            <span class="metadata-label">{{ 'Created' | translate }}:</span>
            <span class="metadata-value">{{ vacancy.created_at | date: 'd MMM y, HH:mm' }}</span>
          </div>
        }
        @if (vacancy.updated_at) {
          <div class="metadata-item">
            <span class="metadata-label">{{ 'Updated' | translate }}:</span>
            <span class="metadata-value">{{ vacancy.updated_at | date: 'd MMM y, HH:mm' }}</span>
          </div>
        }
        @if (vacancy.scraped_at) {
          <div class="metadata-item">
            <span class="metadata-label">{{ 'Scraped' | translate }}:</span>
            <span class="metadata-value">{{ vacancy.scraped_at | date: 'd MMM y, HH:mm' }}</span>
          </div>
        }
        @if (vacancy.enriched_at) {
          <div class="metadata-item">
            <span class="metadata-label">{{ 'Enriched' | translate }}:</span>
            <span class="metadata-value">{{ vacancy.enriched_at | date: 'd MMM y, HH:mm' }}</span>
          </div>
        }
      </div>
    </nb-card-body>
  </nb-card>
}

@if (!vacancy && !isLoading) {
  <nb-card>
    <nb-card-body>
      <p class="text-center">{{ 'Vacancy not found' | translate }}</p>
    </nb-card-body>
  </nb-card>
}
